import { api, endpoints } from "api/api";
import { CourseFormApi } from "api/course-forms/courseformsApi";
import { CourseStatusApi } from "api/course-status";
import { CourseGroupApi } from "api/coursegroups/coursegroupsApi";
import { BaseApiPaging, CourseTrancheApi } from "api/types";
import { AxiosPromise } from "axios";
import { DuplicateCourseValues } from "components/templates";
import { CourseStatus } from "consts/courseType";
import { BaseInformationFormSchema } from "pages/CourseCreate/BaseInformation/BaseInformationStep";
import { CoursePreviewInformationFormSchema } from "pages/CourseCreate/PreviewInformation/PreviewInformationStep";

const fetch = (
  skip: number,
  take: number,
  searchPhrase: string,
  filter: string = "",
  orderBy: string = "",
): AxiosPromise<BaseApiPaging<CourseData>> => {
  return api.get<BaseApiPaging<CourseData>>(endpoints.COURSES.GET(skip, take, searchPhrase, filter, orderBy));
};

const fetchById = (id: number): AxiosPromise<CourseApi> => {
  return api.get<CourseApi>(endpoints.COURSES.GET_BY_ID(id));
};

const changeStatusToPlanned = (
  courseId: number,
  request: {
    status: number;
    dates?: {
      dateOfCourse: Date;
      endOfCourse: Date;
    };
  },
): AxiosPromise<number> => {
  return api.put<number>(endpoints.COURSES.CHANGE_STATUS_TO_PLANNED(courseId), request);
};

const changeStatusToDraft = (courseId: number): AxiosPromise<number> => {
  return api.put<number>(endpoints.COURSES.CHANGE_STATUS_TO_DRAFT(courseId));
};

const changeStatusToArchived = (courseId: number): AxiosPromise<number> => {
  return api.put<number>(endpoints.COURSES.CHANGE_STATUS_TO_ARCHIVED(courseId));
};

const updatePreviewData = (courseId: number, data: CoursePreviewInformationFormSchema): AxiosPromise<number> => {
  return api.put<number>(endpoints.COURSES.UPDATE_PREVIEW_DATA(courseId), data);
};

const changeStatusToReady = (courseId: number): AxiosPromise<number> => {
  return api.put<number>(endpoints.COURSES.CHANGE_STATUS_TO_READY(courseId));
};

const changeStatusToOngoing = (courseId: number): AxiosPromise<number> => {
  return api.put<number>(endpoints.COURSES.CHANGE_STATUS_TO_ONGOING(courseId));
};

const create = (data: BaseInformationFormSchema): AxiosPromise<CreateCourseApiResponse> => {
  const request = {
    name: data.courseName,
    slug: data.slug,
    courseGroupId: Number(data.group),
    categoryId: Number(data.categoryId),
    newGroup: data.newGroup,
    edition: data.edition,
    variant: data.variant,
    // @ts-ignore TODO
    avatar: data.mainImage[0],
  };

  const formData = new FormData();
  Object.entries(request).map(([key, value]) => {
    // TODO Consoldiate mapping
    if (!value) return;
    //@ts-ignore
    formData.append(key, value);
  });

  return api.post<CreateCourseApiResponse>(endpoints.COURSES.CREATE(), formData);
};

const update = (courseId: number, data: BaseInformationFormSchema): AxiosPromise => {
  const { mainImage } = data;
  const formData = new FormData();
  const request = {
    name: data.courseName,
    slug: data.slug,
    courseGroupId: Number(data.group),
    categoryId: Number(data.categoryId),
    newGroup: data.newGroup,
    edition: data.edition,
    variant: data.variant,
  };

  // @ts-ignore TODO
  if (mainImage && typeof mainImage !== "string") formData.append("avatar", mainImage[0] as File);

  Object.entries(request).map(([key, value]) => {
    if (!value) return;
    //@ts-ignore
    formData.append(key, value);
  });

  return api.put(endpoints.COURSES.UPDATE(courseId), formData);
};

const deleteCourse = (courseId: number): Promise<void> => {
  return api.delete(endpoints.COURSES.DELETE(courseId));
};

const duplicate = (courseId: number, body: DuplicateCourseValues): AxiosPromise<CourseApi> => {
  return api.put<CourseApi>(endpoints.COURSES.DUPLICATE(courseId), body);
};

const updateRegistrationSettings = (
  courseId: number,
  body: { registrationsClosed: boolean | null; allowRegistrationAfterStart: boolean },
): AxiosPromise<void> => {
  return api.put(endpoints.COURSES.UPDATE_REGISTRATION_SETTINGS(courseId), body);
};

export const coursesApi = {
  fetch,
  fetchById,
  duplicate,
  create,
  update,
  updatePreviewData,
  delete: deleteCourse,
  changeStatusToPlanned,
  changeStatusToDraft,
  changeStatusToArchived,
  changeStatusToReady,
  changeStatusToOngoing,
  updateRegistrationSettings,
};

export interface CourseApi {
  data: CourseBaseInfoDto;
  tranches: CourseTrancheApi[];
  group: CourseGroupApi;
  forms: CourseFormApi[];
}

export interface CreateCourseApiRequest {
  name: string;
  description?: string;
  newGroup?: string;
  slug: string;
  courseGroupId: number;
  edition: number;
  categoryId: number;
  avatar: File;
}

export interface CourseBaseInfoDto extends CourseStatusApi {
  id: number;
  name: string;
  description: string;
  shortDescription: string;
  agenda: string;
  participationConditions: string;
  courseScope: string;
  organizationInformation: string;
  dateOfCourse: Date;
  endOfCourse: Date;
  createdDate: Date;
  image: string;
  status: CourseStatus;
  slug: string;
  edition: string;
  variant: string;
  endSurvey?: number;
  numberOfStudents: number;
  registrationsClosed: boolean | null;
  allowRegistrationAfterStart: boolean;
  requireFullPaymentForResources: boolean;
}

export interface CreateCourseApiResponse {
  data: CourseBaseInfoDto;
}

export interface CourseData extends CourseStatusApi {
  id: number;
  name: string;
  description: string;
  shortDescription: string;
  agenda: string;
  participationConditions: string;
  courseScope: string;
  organizationInformation: string;
  dateOfCourse: Date;
  endOfCourse: Date;
  createdDate: Date;
  image: string;
  todayPrice: number;
  status: number;
  slug: string;
  edition: number;
  variant: string;
  endSurvey?: number;
  courseGroupId: number;
  courseGroupName: string;
  categoryId: number;
  categoryName: string;
  places: { placeId: number; placeName: string }[];
}
